import {
  Text,
  Button,
  Flex,
  Table,
  Highlight,
  Box
} from '@chakra-ui/react';
import { MdEdit } from 'react-icons/md';
import { CustomButton } from '../UI/CustomButton';
import { useState } from 'react';

type Column = {
  key: string;
  label: string;
  width?: string;
  align?: 'left' | 'right' | 'center' | undefined | string;
};

type RowData = Record<string, React.ReactNode>;

type Action = {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: (row: RowData) => void;
  colorScheme?: string;
};

type Props = {
  columns: Column[];
  data: RowData[];
  onAdd?: () => void;
  onEdit?: (row: RowData) => void;
  actions?: Action[];
  title: string;
};

const renderStatus = (status: string) => {
  switch (status) {
    case 'published':
      return <Highlight styles={{ color: '#66D079', bgColor: '#66D07940', p: '8px', borderRadius: '10px' }} query={status}>{status}</Highlight>;
    case 'draft':
      return <Highlight styles={{ color: '#51CBFD', bgColor: '#51CBFD40', p: '8px' , borderRadius: '10px' }} query={status}>{status}</Highlight>;
    default:
      return <Highlight query={status}>{status}</Highlight>;
  }
};

// const renderSelectors = (selector: string[]) => {
//   return selector.join(", ")
// }

const renderSelectors = (slector: string[]) => {
  if(!slector) return ''

  const chunkSize = 3;

  const chunks = [];
  for (let i = 0; i < slector.length; i += chunkSize) {
    chunks.push(slector.slice(i, i + chunkSize));
  }

  return (
    <Box display='flex' flexDirection='column' gap={2}>
      {chunks.map((group, index) => (
        <Box key={index}>
          {group.join(', ')}
        </Box>
      ))}
    </Box>
  );
};

const selectorsKeys = ['sport', 'flags', 'focus']

export function GenericTable({ columns, data, onAdd, onEdit, actions, title }: Props) {
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  
  return (
    <Flex flexDirection="column" flexGrow={1} overflow='auto' maxW='calc(100vw - 500px)'>
      <Flex justify="space-between" align="center" mt="32px">
        <Text color="#8b8b8b" fontSize="14px">{data.length} {title}</Text>
        {onAdd ? <Button colorScheme="pink" size="sm" h="30px" onClick={onAdd}>Add {title}</Button> : null}
      </Flex>

      <Table.ScrollArea borderWidth="1px" roundedBottom="4px" flexGrow={1} mt="16px" overflowY='auto'>
        <Table.Root stickyHeader interactive>
          <Table.Header>
            <Table.Row>
              {columns.map((col) => (
                <Table.ColumnHeader key={col.key} w={col.width} textAlign={col.align ?? 'left'} height='65px' padding='16px'>
                  {col.label}
                </Table.ColumnHeader>
              ))}
              <Table.ColumnHeader w="50px" />
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {data.map((row, idx) => (
              <Table.Row 
                key={idx}
                onMouseEnter={() => setHoveredRow(idx)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                {columns.map((col) => (
                  <Table.Cell key={col.key} textAlign={col.align ?? 'left'}>
                    {col.key === 'status' && typeof row[col.key] === 'string'
                      ? renderStatus(row[col.key] as string)
                      : selectorsKeys.includes(col.key)
                      ? renderSelectors(row[col.key] as string[])
                      : row[col.key]}
                  </Table.Cell>
                ))}
                <Table.Cell textAlign="right" position="relative">
                  {actions && actions.length > 0 ? (
                    <Flex gap={1} justify="flex-end" opacity={hoveredRow === idx ? 1 : 0} transition="opacity 0.2s">
                      {onEdit && (
                        <CustomButton
                          visual="ghost"
                          onClick={() => {
                            console.log('Edit button clicked in GenericTable, row:', row);
                            onEdit(row);
                          }}
                          aria-label="Edit"
                          size="sm"
                        >
                          <MdEdit />
                        </CustomButton>
                      )}
                      {actions.map((action) => (
                        <CustomButton
                          key={action.key}
                          visual="ghost"
                          onClick={() => action.onClick(row)}
                          aria-label={action.label}
                          size="sm"
                          colorScheme={action.colorScheme}
                        >
                          {action.icon}
                        </CustomButton>
                      ))}
                    </Flex>
                  ) : onEdit ? (
                    <Flex opacity={hoveredRow === idx ? 1 : 0} transition="opacity 0.2s">
                      <CustomButton 
                        visual="ghost" 
                        justifyContent="flex-start"
                        onClick={() => {
                          console.log('Edit button clicked in GenericTable, row:', row);
                          onEdit(row);
                        }}
                      >
                        <MdEdit />
                      </CustomButton>
                    </Flex>
                  ) : null}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Table.ScrollArea>
    </Flex>
  );
}
